import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FUWALO_LOGO_PATH } from "../constants/branding";
import { applySeo } from "../lib/seo";

export const SubscriptionPage: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  useEffect(() => {
    applySeo({
      title: "Fuwalo Star Pricing Preview — Upcoming Subscription Plans",
      description:
        "Preview upcoming Fuwalo Star subscription plans and join the early access waitlist. All features are currently free while pricing is being finalized.",
      canonicalPath: "/subscription",
      ogType: "website",
    });
  }, []);

  const handleNotify = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="page subscription-page">
        <header className="header">
          <div className="header-inner">
            <Link to="/star" className="brand" style={{ textDecoration: "none", color: "inherit" }}>
              <div className="brand-mark brand-mark--logo" aria-hidden="true">
                <img src={FUWALO_LOGO_PATH} alt="" width={32} height={32} />
              </div>
              <span className="brand-name">fuwalo star</span>
            </Link>
          </div>
        </header>
        <main className="subscription-success">
          <div className="subscription-success-card">
            <div className="subscription-success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <path d="M22 4L12 14.01l-3-3" />
              </svg>
            </div>
            <h1>You&apos;re on the list!</h1>
            <p>We&apos;ll notify you when paid plans go live. In the meantime, enjoy all features for free during early access.</p>
            <Link to="/star" className="btn-primary">Back to Fuwalo Star</Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="page subscription-page">
      <header className="header">
        <div className="header-inner header-inner-subscription">
          <Link to="/star" className="brand" style={{ textDecoration: "none", color: "inherit" }}>
            <div className="brand-mark brand-mark--logo" aria-hidden="true">
              <img src={FUWALO_LOGO_PATH} alt="" width={32} height={32} />
            </div>
            <span className="brand-name">fuwalo star</span>
          </Link>
          <div className="header-actions">
            <Link to="/star" className="subscription-back">Back to Fuwalo Star</Link>
          </div>
        </div>
      </header>

      <main className="subscription-main">
        <div className="subscription-early-access-banner">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2l2.4 7.4h7.6l-6 4.6 2.3 7-6.3-4.6-6.3 4.6 2.3-7-6-4.6h7.6z" />
          </svg>
          <span>
            <strong>Early Access</strong> &mdash; All features are currently free. Billing is not active yet.
          </span>
        </div>

        <h1 className="subscription-title">Upcoming Plans</h1>
        <p className="subscription-subtitle">
          Fuwalo Star is in early access &mdash; everything is free right now. Here&apos;s a preview of what paid plans will look like when we launch.
        </p>

        <div className="subscription-plans subscription-plans-three">
          <div className="subscription-plan subscription-plan-free">
            <div className="subscription-plan-badge" style={{ background: "#10b981" }}>Current</div>
            <h3>Early Access</h3>
            <p className="subscription-price">Free</p>
            <ul>
              <li>Unlimited transcriptions</li>
              <li>All features unlocked</li>
              <li>Sheet music export</li>
              <li>MIDI + audio export</li>
              <li>Adjustable sensitivity</li>
            </ul>
            <Link to="/star" className="subscription-plan-btn secondary">You&apos;re using this</Link>
          </div>

          <div className="subscription-plan subscription-plan-standard">
            <h3>Standard</h3>
            <p className="subscription-price">$9<span>/month</span></p>
            <p className="subscription-plan-placeholder">Coming soon</p>
            <ul>
              <li>20 transcriptions per month</li>
              <li>Up to 30 min per file</li>
              <li>Sheet music export</li>
              <li>MIDI + audio export</li>
              <li>Adjustable sensitivity</li>
            </ul>
            <button className="subscription-plan-btn primary" disabled style={{ opacity: 0.5, cursor: "not-allowed" }}>Not available yet</button>
          </div>

          <div className="subscription-plan subscription-plan-pro">
            <div className="subscription-plan-badge">Best Value</div>
            <h3>Pro</h3>
            <p className="subscription-price">$15<span>/month</span></p>
            <p className="subscription-plan-placeholder">Coming soon</p>
            <ul>
              <li>45 transcriptions per month</li>
              <li>Up to 1 hour per file</li>
              <li>Sheet music export</li>
              <li>Batch processing</li>
              <li>Priority queue</li>
              <li>Advanced controls</li>
            </ul>
            <button className="subscription-plan-btn primary" disabled style={{ opacity: 0.5, cursor: "not-allowed" }}>Not available yet</button>
          </div>
        </div>

        <div className="subscription-notify-section" style={{ textAlign: "center", marginTop: "2.5rem" }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "0.5rem" }}>Get notified when plans launch</h2>
          <p style={{ color: "var(--fg-muted)", fontSize: "0.9375rem", marginBottom: "1.25rem" }}>
            Leave your email and we&apos;ll let you know when billing goes live. No spam.
          </p>
          <form onSubmit={handleNotify} style={{ display: "flex", gap: "0.5rem", maxWidth: 420, margin: "0 auto", flexWrap: "wrap", justifyContent: "center" }}>
            <input
              type="text"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="subscription-input"
              style={{ flex: "1 1 140px" }}
            />
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="subscription-input"
              required
              style={{ flex: "1 1 180px" }}
            />
            <button type="submit" className="subscription-plan-btn primary" style={{ flex: "0 0 auto" }}>
              Notify me
            </button>
          </form>
        </div>

        <div className="subscription-features">
          <h2>All paid plans will include</h2>
          <div className="subscription-features-grid">
            <div className="subscription-feature">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>High-accuracy AI transcription</span>
            </div>
            <div className="subscription-feature">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Beautiful sheet music output</span>
            </div>
            <div className="subscription-feature">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>MIDI download for your DAW</span>
            </div>
            <div className="subscription-feature">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Rendered audio preview</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
